import { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import useInvalidateQueries, { InvalidateOptions } from 'hooks/useInvalidateQueries';
import LocationEpisode from 'models/LocationEpisode';
import { locationEpisodeQueryKeys } from 'services/api/locationEpisodes';
import { showLocationEpisode } from 'services/api/locationEpisodes';

type SelectLocationEpisodeOptions = {
  additionalIncludes?: string[];
};

type LocationEpisodeContextType = {
  locationEpisode?: LocationEpisode;
  loading: boolean;
  invalidateData: (options?: InvalidateOptions) => void;
  setSelectedLocationEpisodeId: (locationEpisodeId: string, options?: SelectLocationEpisodeOptions) => void;
  selectedLocationEpisodeId: string;
};

const SelectedLocationEpisodeContext = createContext<LocationEpisodeContextType>({
  locationEpisode: undefined,
  loading: true,
  invalidateData: () => {},
  setSelectedLocationEpisodeId: () => {},
  selectedLocationEpisodeId: '',
});

export function SelectedLocationEpisodeProvider({ children }: { children: React.ReactNode }) {
  const [selectedLocationEpisodeId, setSelectedLocationEpisodeIdState] = useState<string>('');
  const [additionalIncludes, setAdditionalIncludes] = useState<string[]>([]);

  const invalidateQueries = useInvalidateQueries();

  const setSelectedLocationEpisodeId = useCallback(
    (locationEpisodeId: string, options?: SelectLocationEpisodeOptions) => {
      setSelectedLocationEpisodeIdState(locationEpisodeId);
      setAdditionalIncludes(options?.additionalIncludes ?? []);
    },
    []
  );

  const locationEpisodeQueryParams = {
    id: selectedLocationEpisodeId,
    include: [
      'assigned_users.credential',
      'group',
      'group.group_type',
      'last_valid_predecessor',
      'latest_progress_update',
      'owner.client.client_group_types',
      'owner.client.enabled_flags',
      'owner.client.enabled_provider_types',
      'owner.group_type',
      'patient_has_integration_external_id',
      'question_templates',
      'reviews.attrs.attr_values',
      'reviews.group.group_type',
      'reviews.attachment_count',
      'reviews.discharge_checklist.notes',
      'reviews.previous_authorization_review',
      'reviews.summary_job_for_current_attachments',
      'shared_records.owner.client.enabled_flags',
      'shared_records.owner.group_type',
      'shared_records.question_templates',
      'shared_records.assigned_users',
      'shared_records.owner.client.client_group_types',
      'shared_records.reviews.group.group_type',
      'um_ai_summary_enabled',
      ...additionalIncludes,
    ].join(','),
  };

  const enabled = !!selectedLocationEpisodeId;

  const { data: locationEpisode, isFetching: loading } = useQuery({
    queryKey: locationEpisodeQueryKeys.show({ id: selectedLocationEpisodeId, additionalIncludes }),
    queryFn: () => showLocationEpisode(locationEpisodeQueryParams),
    enabled,
  });

  const invalidateData = useCallback(
    (options?: InvalidateOptions) => {
      invalidateQueries({
        locationEpisode: { id: selectedLocationEpisodeId },
        ...options,
      });
    },
    [selectedLocationEpisodeId, invalidateQueries]
  );

  const value = useMemo(
    () => ({
      //using the location episode while the query isn't enabled leads to problems when invalidating
      locationEpisode: enabled ? locationEpisode : undefined,
      setSelectedLocationEpisodeId,
      selectedLocationEpisodeId,
      loading,
      invalidateData,
    }),
    [enabled, locationEpisode, selectedLocationEpisodeId, setSelectedLocationEpisodeId, loading, invalidateData]
  );

  return <SelectedLocationEpisodeContext value={value}>{children}</SelectedLocationEpisodeContext>;
}

export function useSelectedLocationEpisode() {
  const context = useContext(SelectedLocationEpisodeContext);

  if (!context) {
    throw new Error('useSelectedLocationEpisode must be used within a SelectedLocationEpisodeProvider');
  }

  return context;
}
